import gql from 'graphql-tag';

import type { AllInstructionsData, TovInput } from 'src/data/generation';
import type { CustomerPersona } from 'src/data/persona';
import { executeGqlMutation } from 'src/graphql/client';
import { getSingleProp } from 'src/lib/graphql';
import type { Override } from 'src/lib/types';
import type {
  BackendMutationSuggestVariationsArgs,
  BackendSuggestionRequest,
} from 'src/types/backend';

import { FRAGMENT_SUGGEST_REQUEST } from './schema';

const MUTATION = gql`
  mutation SuggestVariations(
    $projectId: ID!
    $assetType: String!
    $suggestionMode: String!
    $keywords: String
    $link: String
    $inputText: String
    $debugAcPrompt: String
    $generateMore: Boolean
    $numberOfVariations: Int
    $language: String
    $suggestionAnnotationId: ID
    $landingPageAssetId: ID
    $forceUsingKeywords: Boolean
    $activeSectionIndex: Int
    $sections: [BlogPostBuilderOutlineSectionInput]
    $rephrase: JSON
    $customerPersonaId: ID
    $tovInput: TovInput
    $allInstructionsData: JSON
    $structureInstructionsData: JSON
  ) {
    suggestVariations(
      projectId: $projectId
      assetType: $assetType
      suggestionMode: $suggestionMode
      keywords: $keywords
      link: $link
      inputText: $inputText
      debugAcPrompt: $debugAcPrompt
      generateMore: $generateMore
      numberOfVariations: $numberOfVariations
      language: $language
      suggestionAnnotationId: $suggestionAnnotationId
      landingPageAssetId: $landingPageAssetId
      forceUsingKeywords: $forceUsingKeywords
      activeSectionIndex: $activeSectionIndex
      sections: $sections
      rephrase: $rephrase
      customerPersonaId: $customerPersonaId
      tovInput: $tovInput
      allInstructionsData: $allInstructionsData
      structureInstructionsData: $structureInstructionsData
    ) {
      ...SuggestionRequest
    }
  }
  ${FRAGMENT_SUGGEST_REQUEST}
`;

type Variables = Override<
  Omit<BackendMutationSuggestVariationsArgs, 'suggestionAnnotationId' | 'tovInput'>,
  { structureInstructionsData?: AllInstructionsData | null }
> & {
  tovInput?: TovInput | null;
  guidanceId?: BackendMutationSuggestVariationsArgs['suggestionAnnotationId'];
  /** Wins over `customerPersonaId`. */
  customerPersona?: CustomerPersona;
};
type Result = BackendSuggestionRequest;

export const suggestVariations = async ({
  guidanceId,
  customerPersona,
  customerPersonaId,
  ...rest
}: Variables) => {
  const response = await executeGqlMutation({
    mutation: MUTATION,
    variables: {
      ...rest,
      suggestionAnnotationId: guidanceId,
      customerPersonaId: customerPersona?.id ?? customerPersonaId,
    },
    options: {
      traceAcRequests: true,
    },
  });
  return getSingleProp<Result>(response.data);
};
